// ======================================================================
// FILE:
// src/modules/service_crm/installed_products/pages/InstalledProductForm.js
//
// MODULE: Service CRM
// FEATURE: Installed Product Form
//
// PHASE 1:
// - Invoice Details
// - Customer & Site
// - Machine Details
// - Save / Update
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

import installedProductService
from "../../services/installedProductService";

import FormSectionCard
from "../../components/FormSectionCard";

import businessPartnerService
from "../../../ppe/pages/masters/business_partner/services/businessPartnerService";

import {
    PANEL_TYPES,
    PANEL_MAKES,
    PHASE_TYPES,
    COOLING_TYPES,
    PLANT_LOCATIONS,
    COMMISSIONING_STATUSES,
    EXECUTIVE_TYPES,
    ENGINE_MAKES,
    CUSTOMER_TYPES,
    getExecutiveNames,
    getEmissionNorms,
    getEngineModels,
    getKvaRatings,
} from "../constants/installedProductMasters";

import {
    getCountries,
    getStates,
    getCities
} from "../constants/geographyMaster";
import {
    DEFAULT_VALUES,
    validateForm,
} from "../constants/installedProductValidation";
// -------------------------------------------------------------------
// FORMAT DATE FOR HTML INPUT
// Converts:
//   Sat, 09 May 2026 00:00:00 GMT
// TO:
//   2026-05-09
// -------------------------------------------------------------------
function formatDateForInput(value) {

  if (!value) return "";

  try {

    const date = new Date(value);

    return date
      .toISOString()
      .split("T")[0];

  } catch {

    return "";
  }
}
export default function InstalledProductForm() {

  const navigate = useNavigate();

  const { id } = useParams();

  const isEdit = !!id;

  // -------------------------------------------------------------------
  // STATE
  // -------------------------------------------------------------------
  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);
  
  const [customerList, setCustomerList] =
    useState([]);

  const [form, setForm] = useState(DEFAULT_VALUES);
  const [errors, setErrors] = useState({});
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  // -------------------------------------------------------------------
  // LOAD DETAIL
  // -------------------------------------------------------------------
  useEffect(() => {

    if (!isEdit) return;

    loadDetail();

  }, [id, isEdit]);

  useEffect(() => {

    loadCustomers();

  }, []);

  // -------------------------------------------------------------------
  // LOAD PRODUCT
  // -------------------------------------------------------------------
  async function loadDetail() {

    try {

      setLoading(true);

      const res =
        await installedProductService
          .getInstalledProduct(id);
        
        console.log("Installed Product:", res);
        console.log("customer_id:", res.customer_id);
          console.log(res);

      setForm((prev) => ({

      ...prev,

      ...res,

      // =========================================================
      // DATE NORMALIZATION
      // =========================================================
      invoice_date:
        formatDateForInput(
          res.invoice_date
        ),
      date_of_installation:
        formatDateForInput(
            res.date_of_installation
      ),  

      commissioning_date:
        formatDateForInput(
          res.commissioning_date
        ),

      warranty_start_date:
        formatDateForInput(
          res.warranty_start_date
        ),

      warranty_end_date:
        formatDateForInput(
          res.warranty_end_date
        ),
    }));
    setSelectedCustomerId(
        res.customer_id
            ? String(res.customer_id)
            : ""
    );

    } finally {

      setLoading(false);
    }
  }

  async function loadCustomerOptions(inputValue) {

    try {

        const result =
            await businessPartnerService
                .getCustomerLookup(inputValue || "");

        return (result || []).map(bp => ({
            value: bp.id,
            label: bp.bp_name,
        }));

    } catch (err) {

        console.error(
            "Customer search failed",
            err
        );

        return [];
    }

}
  
  async function loadCustomers() {

  try {

    const result =
    await businessPartnerService
        .getCustomerLookup("");

    setCustomerList(
    result || []
    );

  } catch (err) {

    console.error(
      "Failed to load customers",
      err
    );

  }

}


  // -------------------------------------------------------------------
  // HANDLE CHANGE
  // -------------------------------------------------------------------
  function handleChange(e) {

    const { name, value } = e.target;

    setForm((prev) => {

        const updated = {
            ...prev,
            [name]: value,
        };
        if (errors[name]) {

            setErrors(prevErrors => ({
                ...prevErrors,
                [name]: "",
            }));

        }

        // Emission Norm changed
        if (name === "emission_norm") {
            updated.engine_model_no = "";
            updated.kva_rating = "";
        }

        // Engine Model changed
        if (name === "engine_model_no") {
            updated.kva_rating = "";
        }

        if (name === "country") {
            updated.state = "";
            updated.city = "";
        }

        if (name === "state") {
            updated.city = "";
        }

        return updated;
    });
}

  async function handleCustomerChange(e) {

  const bpId = e.target.value;
  setSelectedCustomerId(bpId);

  if (!bpId) {

    setForm(prev => ({
    ...prev,
    customer_id: "",
    customer_name: "",
    }));
    console.log("Before clear:", errors);
    setErrors(prev => ({
        ...prev,
        customer_name: "",
    }));
    console.log("Clear called");
    return;
  }

  try {

    const bp =
      await businessPartnerService
        .getBusinessPartner(bpId);
      console.log(bp);
    const defaultContact =
      (bp.contacts || [])
        .find(c => c.is_default)
      ||
      (bp.contacts || [])[0];

    const defaultAddress =
      (bp.addresses || [])
        .find(a => a.is_default)
      ||
      (bp.addresses || [])[0];

    setForm(prev => ({

      ...prev,

      customer_id:
        bp.id,

      customer_name:
        bp.name,

      customer_type:
        bp.partner_type,

      contact_person:
        defaultContact?.contact_name || "",

      contact_number:
        defaultContact?.mobile || "",

      email_id:
        defaultContact?.email || "",

      country:
        defaultAddress?.country || "",

      state:
        defaultAddress?.state || "",

      city:
        defaultAddress?.city || "",

      pin_code:
        defaultAddress?.pincode || "",

      billing_address:
        [
          defaultAddress?.line1,
          defaultAddress?.line2
        ]
        .filter(Boolean)
        .join(", ")

    }));
        setErrors(prev => ({
        ...prev,
        customer_name: "",
    }));

  } catch (err) {

    console.error(
      "Customer load failed",
      err
    );

  }

}

  // -------------------------------------------------------------------
  // VALIDATION
  // -------------------------------------------------------------------
  function validate_old() {

    if (!form.instance_id) {
      alert("Instance ID is required");
      return false;
    }

    if (!form.customer_name) {
      alert("Customer Name is required");
      return false;
    }

    return true;
  }
  function validate_old2() {

    const result = validateForm(form);

    if (!result.isValid) {

        alert(
            Object.values(result.errors).join("\n")
        );

        return false;
    }

    return true;
}
function validate() {

    const result = validateForm(form);

      console.log(result);

    setErrors(result.errors);

    return result.isValid;
}

function getFieldClass(fieldName, baseClass = "form-control") {

    return `${baseClass} ${
        errors[fieldName] ? "is-invalid" : ""
    }`;

}
  // -------------------------------------------------------------------
  // SAVE
  // -------------------------------------------------------------------
  async function handleSave() {

    if (!validate()) return;

    try {

      setSaving(true);

      if (isEdit) {

        await installedProductService
          .updateInstalledProduct(
            id,
            form
          );

        alert(
          "Installed product updated successfully"
        );

      } else {

        await installedProductService
          .createInstalledProduct(
            form
          );

        alert(
          "Installed product created successfully"
        );
      }

      navigate(
        "/service-crm/installed-products"
      );

    } catch (err) {

      alert(
        err?.response?.data?.message ||
        "Failed to save installed product"
      );

    } finally {

      setSaving(false);
    }
  }

  // -------------------------------------------------------------------
  // COMMON INPUT STYLE
  // -------------------------------------------------------------------
  const gridStyle = {
    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(240px, 1fr))",

    gap: 16,
  };

  // -------------------------------------------------------------------
  // LOADING
  // -------------------------------------------------------------------
  if (loading) {

    return (

      <div
        style={{
          padding: 40,
        }}
      >
        Loading installed product...
      </div>
    );
  }

  // -------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------
  return (

    <div>

      {/* ========================================================= */}
      {/* PAGE HEADER */}
      {/* ========================================================= */}

      <div
        style={{
          background:
            "linear-gradient(90deg, #0d6efd, #3d8bfd)",

          borderRadius: 14,

          padding: "16px 22px",

          color: "#fff",

          marginBottom: 20,
        }}
      >

        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
          }}
        >
          {isEdit
            ? "Edit Installed Product"
            : "Add Installed Product"}
        </div>

        <div
          style={{
            fontSize: 13,
            opacity: 0.9,
          }}
        >
          Manage installed machine registration
          and commissioning details
        </div>

      </div>

      {/* ========================================================= */}
      {/* INVOICE DETAILS */}
      {/* ========================================================= */}

      <FormSectionCard
        title="Invoice Details"
        collapsible
        defaultExpanded={true}
        >

        <div style={gridStyle}>

          <div>
            <label className="form-label">
              Invoice Number
            </label>

            <input
                type="text"
                className={getFieldClass("invoice_number")}
                name="invoice_number"
                value={form.invoice_number}
                onChange={handleChange}
            />

            {errors.invoice_number && (
                <div className="invalid-feedback">
                    {errors.invoice_number}
                </div>
            )}
          </div>

          <div>
            <label className="form-label">
              Invoice Date
            </label>

            <input
              type="date"
              className={getFieldClass("invoice_date")}
              name="invoice_date"
              value={form.invoice_date || ""}
              onChange={handleChange}
          />

          {errors.invoice_date && (
              <div className="invalid-feedback">
                  {errors.invoice_date}
              </div>
          )}
          </div>

          <div>
            <label className="form-label">
              OA Number
            </label>

            <input
                type="text"
                className={getFieldClass("oa_number")}
                name="oa_number"
                value={form.oa_number}
                onChange={handleChange}
            />

            {errors.oa_number && (
                <div className="invalid-feedback">
                    {errors.oa_number}
                </div>
            )}
          </div>

          

          <div>
            <label className="form-label">
              Canopy Number
            </label>

            <input
              type="text"
              className={getFieldClass("canopy_number")}
              name="canopy_number"
              value={form.canopy_number}
              onChange={handleChange}
            />
            {errors.canopy_number && (
                <div className="invalid-feedback">
                    {errors.canopy_number}
                </div>
            )}
          </div>

        </div>

      </FormSectionCard>

      {/* ========================================================= */}
      {/* CUSTOMER & SITE */}
      {/* ========================================================= */}

      <FormSectionCard
        title="Customer & Site"
        collapsible
        defaultExpanded={false}
      >

        <div style={gridStyle}>

          <div>
            <label className="form-label">
              Customer Name *
            </label>

            <AsyncSelect
                classNamePrefix="react-select"
                placeholder="Select Customer..."
                isClearable
                cacheOptions
                //defaultOptions
                loadOptions={loadCustomerOptions}
                value={
                    selectedCustomerId
                        ? {
                            value: selectedCustomerId,
                            label: form.customer_name,
                        }
                        : null
                }
                onChange={(selected) =>
                    handleCustomerChange({
                        target: {
                            value: selected ? selected.value : "",
                        },
                    })
                }
            />
            <div style={{ color: "red", fontSize: 12 }}>
                {errors.customer_name}
            </div>
          </div>

          <div>
            <label className="form-label">
                Customer Type
            </label>

            <select
                className={getFieldClass(
                    "customer_type",
                    "form-select"
                )}
                name="customer_type"
                value={form.customer_type}
                onChange={handleChange}
            >
                {CUSTOMER_TYPES.map((option) => (
                    <option
                        key={option.value}
                        value={option.value}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
            {errors.customer_type && (
                <div className="invalid-feedback">
                    {errors.customer_type}
                </div>
            )}
        </div>

          <div>
            <label className="form-label">
              Contact Person
            </label>

            <input
              type="text"
              className={getFieldClass("contact_person")}
              name="contact_person"
              value={form.contact_person}
              onChange={handleChange}
            />
            {errors.contact_person && (
                <div className="invalid-feedback">
                    {errors.contact_person}
                </div>
            )}
          </div>

          <div>
            <label className="form-label">
              Contact Number
            </label>

            <input
              type="text"
              className={getFieldClass("contact_number")}
              name="contact_number"
              value={form.contact_number}
              onChange={handleChange}
            />
            {errors.contact_number && (
                <div className="invalid-feedback">
                    {errors.contact_number}
                </div>
            )}
          </div>

          <div>
            <label className="form-label">
              Email ID
            </label>

            <input
              type="email"
              className={getFieldClass("email_id")}
              name="email_id"
              value={form.email_id}
              onChange={handleChange}
            />
            {errors.email_id && (
                <div className="invalid-feedback">
                    {errors.email_id}
                </div>
            )}
          </div>

          <div>
            <label className="form-label">
                Country
            </label>

            <select
                className={getFieldClass(
                    "country",
                    "form-select"
                )}
                name="country"
                value={form.country}
                onChange={handleChange}
            >
                {getCountries().map((option) => (
                    <option
                        key={option.value}
                        value={option.value}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
            {errors.country && (
                <div className="invalid-feedback">
                    {errors.country}
                </div>
            )}
        </div>

          <div>
            <label className="form-label">
                State
            </label>

            <select
                className={getFieldClass(
                    "state",
                    "form-select"
                )}
                name="state"
                value={form.state}
                onChange={handleChange}
            >
                {getStates(form.country).map((option) => (
                    <option
                        key={option.value}
                        value={option.value}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
                        {errors.state && (
                <div className="invalid-feedback">
                    {errors.state}
                </div>
            )}
        </div>

          <div>
            <label className="form-label">
                City
            </label>

            <select
                className={getFieldClass(
                    "city",
                    "form-select"
                )}
                name="city"
                value={form.city}
                onChange={handleChange}
            >
                {getCities(form.country, form.state).map((option) => (
                    <option
                        key={option.value}
                        value={option.value}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
                        {errors.city && (
                <div className="invalid-feedback">
                    {errors.city}
                </div>
            )}
        </div>

          <div>
            <label className="form-label">
              PIN Code
            </label>

            <input
              type="text"
              className={getFieldClass("pin_code")}
              name="pin_code"
              value={form.pin_code}
              
              onChange={handleChange}
            />
            {errors.pin_code && (
                <div className="invalid-feedback">
                    {errors.pin_code}
                </div>
            )}
          </div>

        </div>

        {/* ADDRESSES */}

        <div
          style={{
            marginTop: 18,
          }}
        >

          <div
            style={{
              marginBottom: 14,
            }}
          >

            <label className="form-label">
              Billing Address
            </label>

            <textarea
              className={getFieldClass("billing_address")}
              rows={3}
              name="billing_address"
              value={form.billing_address}
            
              onChange={handleChange}
            />
            {errors.billing_address && (
                <div className="invalid-feedback">
                    {errors.billing_address}
                </div>
            )}

          </div>

          <div>

            <label className="form-label">
              Site Address
            </label>

            <textarea
              className={getFieldClass("site_address")}
              rows={3}
              name="site_address"
              value={form.site_address}
              onChange={handleChange}
            />
            {errors.site_address && (
                <div className="invalid-feedback">
                    {errors.site_address}
                </div>
            )}

          </div>

        </div>

      </FormSectionCard>

      {/* ========================================================= */}
      {/* MACHINE DETAILS */}
      {/* ========================================================= */}

      <FormSectionCard
          title="Machine Details"
          collapsible
          defaultExpanded={false}
      >
    <div style={gridStyle}>

        {/* Row 1 - Dependency Chain */}

        <div>
            <label className="form-label">
                Emission Norm
            </label>

            <select
                className={getFieldClass(
                    "emission_norm",
                    "form-select"
                )}
                name="emission_norm"
                value={form.emission_norm}
                onChange={handleChange}
            >
                {getEmissionNorms().map((option) => (
                    <option
                        key={option.value}
                        value={option.value}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
                        {errors.emission_norm && (
                <div className="invalid-feedback">
                    {errors.emission_norm}
                </div>
            )}
        </div>

        

        <div>
            <label className="form-label">
                Engine Model
            </label>

            <select
              className={getFieldClass(
                  "engine_model_no",
                  "form-select"
              )}
              name="engine_model_no"
                value={form.engine_model_no}
                onChange={handleChange}
            >
                {getEngineModels(
                    form.emission_norm
                    
                ).map((option) => (
                    <option
                        key={option.value}
                        value={option.value}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
            {errors.engine_model_no && (
                <div className="invalid-feedback">
                    {errors.engine_model_no}
                </div>
            )}
        </div>

        <div>
            <label className="form-label">
                KVA Rating
            </label>

            <select
                className={getFieldClass(
                    "kva_rating",
                    "form-select"
                )}
                name="kva_rating"
                value={form.kva_rating}
                onChange={handleChange}
            >
                {getKvaRatings(
                    form.emission_norm,
                    form.engine_model_no,
                    
                ).map((option) => (
                    <option
                        key={option.value}
                        value={option.value}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
            {errors.kva_rating && (
                <div className="invalid-feedback">
                    {errors.kva_rating}
                </div>
            )}
        </div>

        {/* Row 2 - Engine Information */}

        <div>
            <label className="form-label">
                Engine App Code
            </label>

            <input
            type="text"
            className={getFieldClass("engine_app_code")}
            name="engine_app_code"
                value={form.engine_app_code}
                onChange={handleChange}
            />
            {errors.engine_app_code && (
                <div className="invalid-feedback">
                    {errors.engine_app_code}
                </div>
            )}
        </div>

        <div>
            <label className="form-label">
                Engine Serial No
            </label>

            <input
            type="text"
            className={getFieldClass("engine_serial_no")}
            name="engine_serial_no"
                value={form.engine_serial_no}
                onChange={handleChange}
            />
            {errors.engine_serial_no && (
                <div className="invalid-feedback">
                    {errors.engine_serial_no}
                </div>
            )}
        </div>

        <div>
            <label className="form-label">
                Engine KRM No
            </label>

            <input
            type="text"
            className={getFieldClass("engine_krm_no")}
            name="engine_krm_no"
                value={form.engine_krm_no}
                onChange={handleChange}
            />
            {errors.engine_krm_no && (
                <div className="invalid-feedback">
                    {errors.engine_krm_no}
                </div>
            )}
        </div>
        
        <div>
            <label className="form-label">
              Instance ID *
            </label>

            <input
            type="text"
            className={getFieldClass("instance_id")}
            name="instance_id"
              value={form.instance_id}
              onChange={handleChange}
              placeholder="Auto-generated (editable)"
            />
            {errors.instance_id && (
                <div className="invalid-feedback">
                    {errors.instance_id}
                </div>
            )}
          </div>
        
        
        <div>
            <label className="form-label">
                Engine Make
            </label>

            <select
            className={getFieldClass(
                "engine_make",
                "form-select"
            )}
            name="engine_make"
              value={form.engine_make}
              onChange={handleChange}
          >
              {ENGINE_MAKES.map((option) => (
                  <option
                      key={option.value}
                      value={option.value}
                  >
                      {option.label}
                  </option>
              ))}
          </select>
          {errors.engine_make && (
              <div className="invalid-feedback">
                  {errors.engine_make}
              </div>
          )}
        </div>

    </div>
</FormSectionCard>

    
    {/* ========================================================= */}
    {/* ALTERNATOR DETAILS */}
    {/* ========================================================= */}

    <FormSectionCard
    title="Alternator Details"
    collapsible
    defaultExpanded={false}
    >

    <div style={gridStyle}>

        <div>
        <label className="form-label">
            Alternator Model
        </label>

       <input
        type="text"
        className={getFieldClass("alternator_model")}
        name="alternator_model"
            value={form.alternator_model}
            onChange={handleChange}
        />
        {errors.alternator_model && (
            <div className="invalid-feedback">
                {errors.alternator_model}
            </div>
        )}
        </div>

        <div>
        <label className="form-label">
            Alternator Number
        </label>

        <input
        type="text"
        className={getFieldClass("alternator_number")}
        name="alternator_number"
            value={form.alternator_number}
            onChange={handleChange}
        />
        {errors.alternator_number && (
            <div className="invalid-feedback">
                {errors.alternator_number}
            </div>
        )}
        </div>

    </div>

    </FormSectionCard>


    {/* ========================================================= */}
    {/* PANEL DETAILS */}
    {/* ========================================================= */}

    <FormSectionCard
    title="Panel Details"
    collapsible
    defaultExpanded={false}
    >

    <div style={gridStyle}>

        <div>
        <label className="form-label">
            Panel Type
        </label>

        <select
          className={getFieldClass(
              "panel_type",
              "form-select"
          )}
          name="panel_type"
          value={form.panel_type}
          onChange={handleChange}
      >
          {PANEL_TYPES.map((option) => (
              <option
                  key={option.value}
                  value={option.value}
              >
                  {option.label}
              </option>
          ))}
      </select>
      {errors.panel_type && (
          <div className="invalid-feedback">
              {errors.panel_type}
          </div>
      )}
        </div>

        <div>
        <label className="form-label">
            Panel Serial No
        </label>

        <input
            type="text"
            className={getFieldClass("panel_serial_no")}
            name="panel_serial_no"
            value={form.panel_serial_no}
            onChange={handleChange}
        />
        {errors.panel_serial_no && (
            <div className="invalid-feedback">
                {errors.panel_serial_no}
            </div>
        )}
        </div>

        <div>
        <label className="form-label">
            Panel Make
        </label>

        <select
            className={getFieldClass(
                "panel_make",
                "form-select"
            )}
            name="panel_make"
            value={form.panel_make}
            onChange={handleChange}
        >
            {PANEL_MAKES.map((option) => (
                <option
                    key={option.value}
                    value={option.value}
                >
                    {option.label}
                </option>
            ))}
        </select>
        {errors.panel_make && (
            <div className="invalid-feedback">
                {errors.panel_make}
            </div>
        )}
        </div>

        <div>
        <label className="form-label">
            Phase
        </label>

        <select
            className={getFieldClass(
                "phase",
                "form-select"
            )}
            name="phase"
            value={form.phase}
            onChange={handleChange}
        >
            {PHASE_TYPES.map((option) => (
                <option
                    key={option.value}
                    value={option.value}
                >
                    {option.label}
                </option>
            ))}
        </select>
        {errors.phase && (
            <div className="invalid-feedback">
                {errors.phase}
            </div>
        )}
        </div>

    </div>

    </FormSectionCard>


    {/* ========================================================= */}
    {/* TECHNICAL DETAILS */}
    {/* ========================================================= */}

    <FormSectionCard
        title="Technical Details"
        collapsible
        defaultExpanded={false}
    >

        <div style={gridStyle}>

            <div>
                <label className="form-label">
                    Cooling Type
                </label>

                <select
                className={getFieldClass(
                    "cooling_type",
                    "form-select"
                )}
                name="cooling_type"
                    value={form.cooling_type}
                    onChange={handleChange}
                >
                    {COOLING_TYPES.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                        >
                            {option.label}
                        </option>
                    ))}
                </select>
                {errors.cooling_type && (
                    <div className="invalid-feedback">
                        {errors.cooling_type}
                    </div>
                )}
            </div>

            <div>
                <label className="form-label">
                    Running Hours
                </label>

                <input
                type="number"
                className={getFieldClass("running_hours")}
                name="running_hours"
                    name="running_hours"
                    value={form.running_hours}
                    onChange={handleChange}
                />
                {errors.running_hours && (
                    <div className="invalid-feedback">
                        {errors.running_hours}
                    </div>
                )}
            </div>

            <div>
                <label className="form-label">
                    Battery Serial No
                </label>

                <input
                type="text"
                className={getFieldClass("battery_serial_no")}
                name="battery_serial_no"
                    value={form.battery_serial_no}
                    onChange={handleChange}
                />
                {errors.battery_serial_no && (
                    <div className="invalid-feedback">
                        {errors.battery_serial_no}
                    </div>
                )}
            </div>
            <div>
              <label className="form-label">
                  Tested By
              </label>

              <input
              type="text"
              className={getFieldClass("tested_by")}
              name="tested_by"
                  value={form.tested_by}
                  onChange={handleChange}
              />
              {errors.tested_by && (
                  <div className="invalid-feedback">
                      {errors.tested_by}
                  </div>
              )}
              </div>

        </div>

    </FormSectionCard>


    {/* ========================================================= */}
    {/* INTERNAL DETAILS */}
    {/* ========================================================= */}

    <FormSectionCard
    title="Internal Details"
    collapsible
    defaultExpanded={false}
    >

    <div style={gridStyle}>

        

        <div>
          <label className="form-label">
              Executive Type
          </label>

          <select
            className={getFieldClass(
                "kgd_jakson_executive",
                "form-select"
            )}
            name="kgd_jakson_executive"
              value={form.kgd_jakson_executive}
              onChange={handleChange}
          >
              {EXECUTIVE_TYPES.map((option) => (
                  <option
                      key={option.value}
                      value={option.value}
                  >
                      {option.label}
                  </option>
              ))}
          </select>
                    {errors.kgd_jakson_executive && (
              <div className="invalid-feedback">
                  {errors.kgd_jakson_executive}
              </div>
          )}
      </div>

       <div>
          <label className="form-label">
              Executive Name
          </label>

          <select
              className={getFieldClass(
                  "executive_name",
                  "form-select"
              )}
              name="executive_name"
              value={form.executive_name}
              onChange={handleChange}
          >
              {getExecutiveNames(
                  form.kgd_jakson_executive
              ).map((option) => (
                  <option
                      key={option.value}
                      value={option.value}
                  >
                      {option.label}
                  </option>
              ))}
          </select>
          {errors.executive_name && (
              <div className="invalid-feedback">
                  {errors.executive_name}
              </div>
          )}
      </div>

        

        <div>
          <label className="form-label">
              Plant Location
          </label>

          <select
              className={getFieldClass(
                  "plant_location",
                  "form-select"
              )}
              name="plant_location"
              value={form.plant_location}
              onChange={handleChange}
          >
              {PLANT_LOCATIONS.map((option) => (
                  <option
                      key={option.value}
                      value={option.value}
                  >
                      {option.label}
                  </option>
              ))}
          </select>
          {errors.plant_location && (
              <div className="invalid-feedback">
                  {errors.plant_location}
              </div>
          )}
      </div>

    </div>

    </FormSectionCard>


    {/* ========================================================= */}
    {/* COMMISSIONING */}
    {/* ========================================================= */}

    <FormSectionCard
    title="Commissioning"
    collapsible
    defaultExpanded={false}
    >

    <div style={gridStyle}>

        <div>
          <label className="form-label">
              Commissioning Status
          </label>

          <select
              className={getFieldClass(
                  "commissioning_status",
                  "form-select"
              )}
              name="commissioning_status"
              value={form.commissioning_status}
              onChange={handleChange}
          >
              {COMMISSIONING_STATUSES.map((option) => (
                  <option
                      key={option.value}
                      value={option.value}
                  >
                      {option.label}
                  </option>
              ))}
          </select>
          {errors.commissioning_status && (
              <div className="invalid-feedback">
                  {errors.commissioning_status}
              </div>
          )}
      </div>

        {/* Date of Installation */}
        <div>
            <label className="form-label">
                Date of Installation
            </label>

            <input
                type="date"
                className={getFieldClass("date_of_installation")}
                name="date_of_installation"
                value={form.date_of_installation}
                onChange={handleChange}
            />
            {errors.date_of_installation && (
                <div className="invalid-feedback">
                    {errors.date_of_installation}
                </div>
            )}
        </div>

        
        <div>
        <label className="form-label">
            Commissioning Date
        </label>

        <input
            type="date"
            className={getFieldClass("commissioning_date")}
            name="commissioning_date"
            value={form.commissioning_date || ""}
            onChange={handleChange}
        />
        {errors.commissioning_date && (
            <div className="invalid-feedback">
                {errors.commissioning_date}
            </div>
        )}
        </div>
        <div>
        <label className="form-label">
            Service Dealer
        </label>

        <input
            type="text"
            className={getFieldClass("service_dealer")}
            name="service_dealer"
            value={form.service_dealer}
            onChange={handleChange}
        />
        {errors.service_dealer && (
            <div className="invalid-feedback">
                {errors.service_dealer}
            </div>
        )}
        </div>

    </div>

    </FormSectionCard>


    {/* ========================================================= */}
    {/* WARRANTY & AMC */}
    {/* ========================================================= */}

    <FormSectionCard
    title="Warranty & AMC"
    collapsible
    defaultExpanded={false}
    >

    <div style={gridStyle}>

        <div>
        <label className="form-label">
            Warranty Start Date
        </label>

        <input
            type="date"
            className="form-control"
            name="warranty_start_date"
            value={form.warranty_start_date || ""}
            onChange={handleChange}
        />
        </div>

        <div>
        <label className="form-label">
            Warranty End Date
        </label>

        <input
            type="date"
            className="form-control"
            name="warranty_end_date"
            value={form.warranty_end_date || ""}
            onChange={handleChange}
        />
        </div>

        <div>
        <label className="form-label">
            AMC Status
        </label>

        <select
            className="form-select"
            name="amc_status"
            value={form.amc_status}
            onChange={handleChange}
        >
            <option value="">
            Select
            </option>

            <option value="Active">
            Active
            </option>

            <option value="Expired">
            Expired
            </option>

            <option value="Not Applicable">
            Not Applicable
            </option>

        </select>
        </div>

    </div>

    </FormSectionCard>

      
      {/* ========================================================= */}
      {/* ACTIONS */}
      {/* ========================================================= */}

      <div
        style={{
          display: "flex",

          justifyContent: "flex-end",

          gap: 12,

          marginBottom: 40,
        }}
      >

        {/* CANCEL */}
        <button
          type="button"

          className="btn btn-light"

          onClick={() =>
            navigate(
              "/service-crm/installed-products"
            )
          }
        >
          Cancel
        </button>

        {/* SAVE */}
        <button
          type="button"

          className="btn btn-primary"

          disabled={saving}

          onClick={handleSave}
        >
          {saving
            ? "Saving..."
            : isEdit
              ? "Update Product"
              : "Save Product"}
        </button>

      </div>

    </div>
  );
}